import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

export interface DescriptionItem {
  term: ReactNode;
  description: ReactNode;
  /** Optional testid placed on the description value (for e2e / assertions). */
  testId?: string;
}

const Dl = chakra('dl', { base: { margin: 0, minWidth: 0 } });

const Row = chakra('div', {
  base: {
    display: 'grid',
    gap: '1',
    py: '2',
    minWidth: 0,
    borderBottomWidth: '1px',
    borderColor: 'border.subtle',
    _last: { borderBottomWidth: 0 },
  },
});

const Term = chakra('dt', {
  base: { fontFamily: 'body', fontSize: 'xs', fontWeight: 'medium', color: 'text.muted' },
});

const Desc = chakra('dd', {
  base: { margin: 0, fontFamily: 'body', fontSize: 'md', color: 'text.primary', minWidth: 0 },
});

type Layout = 'stacked' | 'inline';

export interface DescriptionListProps extends Omit<React.HTMLAttributes<HTMLDListElement>, 'color'> {
  items: DescriptionItem[];
  /** `stacked` = term above value; `inline` = term left, value right (key/value rows). */
  layout?: Layout;
}

/**
 * A semantic key/value list (`<dl>`), the canonical way to present run/agent metadata.
 * `inline` gives the classic label-left / value-right pairing used in detail panels.
 */
export const DescriptionList = forwardRef<HTMLDListElement, DescriptionListProps>(
  function DescriptionList({ items, layout = 'stacked', ...rest }, ref) {
    const rowStyle: HTMLChakraProps<'div'> =
      layout === 'inline'
        ? { gridTemplateColumns: 'minmax(0, auto) 1fr', alignItems: 'baseline', gap: '4' }
        : {};
    const descStyle: HTMLChakraProps<'dd'> =
      layout === 'inline' ? { textAlign: 'end', fontWeight: 'medium' } : {};
    return (
      <Dl ref={ref} {...rest}>
        {items.map((item, i) => (
          <Row key={i} {...rowStyle}>
            <Term>{item.term}</Term>
            <Desc data-testid={item.testId} {...descStyle}>
              {item.description}
            </Desc>
          </Row>
        ))}
      </Dl>
    );
  },
);
